import React from 'react'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Users } from 'lucide-react'
import { useClientTeamMembers } from '@/hooks/useClientTeamMembers'
import type { Territory } from '@/types/client'

interface SlackChannelMemberSelectorProps {
  clientId: string
  // 'main' and 'reporting' are retired -- kept so existing callers/stored values
  // still typecheck, but the client wizard no longer renders those channels.
  channelType: 'main' | 'reporting' | 'sales' | 'positive_replies' | 'neutral_negative' | 'neutral_negative_replies' | 'meetings_booked' | 'website_identification'
  territoryId?: string
  territoryName?: string
  existingMemberIds?: string[]
  selectedMemberIds: string[]
  onSelectionChange: (memberIds: string[]) => void
  territories?: Territory[]
  productData?: { products: any[], territories: Territory[] }
  formDataTeamMembers?: any[] // Fallback team members from formData
}

export function SlackChannelMemberSelector({
  clientId,
  channelType,
  selectedMemberIds,
  onSelectionChange,
  formDataTeamMembers,
}: SlackChannelMemberSelectorProps) {
  const { teamMembers: dbTeamMembers, loading } = useClientTeamMembers(clientId)
  
  // Create a unique prefix for this selector instance to prevent ID collisions
  const selectorId = `${channelType}-${clientId}`
  
  // Merge database team members with formData team members (formData takes priority for IDs that match)
  // Convert formData team members to ClientTeamMember format
  const formMembers = (formDataTeamMembers || []).map((member: any) => ({
    id: member.id,
    client_id: clientId,
    name: member.name,
    email: member.email,
    title: member.title || null,
    tags: member.tags || [],
    is_primary_contact: member.is_primary_contact || false,
    is_billing_contact: member.is_billing_contact || false,
    status: 'active' as const,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }))
  
  // Merge: prefer formData members, then add any DB members not in formData
  const formMemberIds = new Set(formMembers.map(m => m.id))
  const allTeamMembers = [
    ...formMembers,
    ...dbTeamMembers.filter(m => !formMemberIds.has(m.id))
  ]
  
  // Filter to only active team members
  const activeMembers = allTeamMembers.filter(m => m.status === 'active')
  
  // Auto-select all team members for main channel by default if none selected
  // Only run once when component mounts with empty selection
  const hasAutoSelected = React.useRef(false)
  React.useEffect(() => {
    if (channelType === 'main' && !hasAutoSelected.current && selectedMemberIds.length === 0 && activeMembers.length > 0) {
      const allMemberIds = activeMembers.map(m => m.id)
      onSelectionChange(allMemberIds)
      hasAutoSelected.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelType])
  
  // Reset the ref if activeMembers change (new members added)
  React.useEffect(() => {
    if (activeMembers.length > 0) {
      // Allow re-auto-selection if selection becomes empty again
      if (selectedMemberIds.length === 0 && channelType === 'main') {
        hasAutoSelected.current = false
      }
    }
  }, [activeMembers.length, selectedMemberIds.length, channelType])
  
  const toggleMember = (memberId: string) => {
    if (selectedMemberIds.includes(memberId)) {
      onSelectionChange(selectedMemberIds.filter(id => id !== memberId))
    } else {
      onSelectionChange([...selectedMemberIds, memberId])
    }
  }
  
  const toggleAll = () => {
    const allMemberIds = activeMembers.map(m => m.id)
    const allSelected = allMemberIds.every(id => selectedMemberIds.includes(id))
    
    if (allSelected) {
      onSelectionChange([])
    } else {
      onSelectionChange(allMemberIds)
    }
  }
  
  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading team members...</div>
  }
  
  if (activeMembers.length === 0) {
    return (
      <div className="text-sm text-muted-foreground p-4 border rounded-lg">
        No team members found. Add team members first to assign them to channels.
      </div>
    )
  }
  
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm font-medium">Select Team Members</Label>
        </div>
        <button
          type="button"
          onClick={toggleAll}
          className="text-xs text-primary hover:underline"
        >
          {activeMembers.every(m => selectedMemberIds.includes(m.id)) ? 'Deselect All' : 'Select All'}
        </button>
      </div>
      
      <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-3">
        {activeMembers.map(member => {
          const checkboxId = `${selectorId}-member-${member.id}`
          return (
          <div key={member.id} className="flex items-center space-x-2 py-1">
            <Checkbox
              id={checkboxId}
              checked={selectedMemberIds.includes(member.id)}
              onCheckedChange={() => toggleMember(member.id)}
            />
            <Label htmlFor={checkboxId} className="text-sm cursor-pointer flex-1">
              <div className="font-medium">{member.name}</div>
              {member.title && (
                <div className="text-xs text-muted-foreground">{member.title}</div>
              )}
              {member.email && (
                <div className="text-xs text-muted-foreground">{member.email}</div>
              )}
            </Label>
          </div>
          )
        })}
      </div>
      
      {selectedMemberIds.length > 0 && (
        <div className="text-sm text-muted-foreground">
          {selectedMemberIds.length} team member{selectedMemberIds.length !== 1 ? 's' : ''} selected
        </div>
      )}
    </div>
  )
}

